import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { I18nProvider } from "@/i18n";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ClientLayout from "@/components/Layout";
import AdminLayout from "@/components/AdminLayout";
import Dashboard from "@/pages/Dashboard";
import AccountDetail from "@/pages/AccountDetail";
import Transfer from "@/pages/Transfer";
import Send from "@/pages/Send";
import Cards from "@/pages/Cards";
import Merchant from "@/pages/Merchant";
import Checkout from "@/pages/Checkout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminCurrencies from "@/pages/admin/AdminCurrencies";
import AdminRates from "@/pages/admin/AdminRates";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminGateway from "@/pages/admin/AdminGateway";
import AdminMerchants from "@/pages/admin/AdminMerchants";

function FullScreenLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-black">
      <div className="w-48 ob-loader h-1 rounded-full bg-zinc-900" />
    </div>
  );
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <FullScreenLoader />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AdminProtected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <FullScreenLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;
  return children;
}

function AppRoutes() {
  const { user, loading } = useAuth();
  if (loading) return <FullScreenLoader />;
  const landing = user?.role === "admin" ? "/admin" : "/";

  return (
    <Routes>
      {/* Public hosted checkout */}
      <Route path="/pay/:id" element={<Checkout />} />

      <Route path="/login" element={user ? <Navigate to={landing} replace /> : <Login />} />
      <Route path="/register" element={user ? <Navigate to={landing} replace /> : <Register />} />

      {/* Client panel */}
      <Route element={<Protected><ClientLayout /></Protected>}>
        <Route path="/" element={<Dashboard />} />
        <Route path="/accounts/:id" element={<AccountDetail />} />
        <Route path="/transfer" element={<Transfer />} />
        <Route path="/send" element={<Send />} />
        <Route path="/cards" element={<Cards />} />
        <Route path="/merchant" element={<Merchant />} />
      </Route>

      {/* Admin panel */}
      <Route element={<AdminProtected><AdminLayout /></AdminProtected>}>
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/admin/currencies" element={<AdminCurrencies />} />
        <Route path="/admin/rates" element={<AdminRates />} />
        <Route path="/admin/users" element={<AdminUsers />} />
        <Route path="/admin/gateway" element={<AdminGateway />} />
        <Route path="/admin/merchants" element={<AdminMerchants />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App min-h-screen bg-black">
      <I18nProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
          <Toaster position="top-center" theme="dark" richColors />
        </AuthProvider>
      </I18nProvider>
    </div>
  );
}
